import type { JSX, PropsWithChildren } from 'react';

import { Link } from '@change/ds-core-ui/next';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { getPetitionQRCode } from 'src/app/shared/hooks/qrcode';

import { useAsyncDownload } from '../hooks/useAsyncDownload';

/**
 * Render a link that when clicked, downloads a QR code.
 * The QR code is fetched lazily when the link is clicked.
 */
export function PetitionQRCodeDownloadLink({
	children,
	slugOrId,
	size,
	type,
	filename,
	onDownload,
}: PropsWithChildren<{
	slugOrId: string;
	type: QrCodeType;
	size?: number;
	filename?: string;
	onDownload?: () => Promise<void>;
}>): JSX.Element {
	const utilityContext = useUtilityContext();

	const { actions } = useAsyncDownload({
		fetchData: async () => {
			const result = await getPetitionQRCode({
				slugOrId,
				type,
				size,
				utilityContext,
			});
			return { dataUri: result.imageBase64Uri };
		},
		filename: filename || 'petition-qrcode.png',
		onDownload,
	});

	return (
		<Link
			href="#"
			onClick={actions.handleDownload}
			bold
			size="small"
			variant="secondary"
			data-qa="qrcode-download-link"
			data-testid="qrcode-download-link"
			preventScrollReset
		>
			{children}
		</Link>
	);
}
